import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { variantImageService } from '@/services/admin/variantImageService';

export const useVariantImages = (
  productId: string | undefined,
  potSize: string | undefined,
  potType: string | undefined,
  potColor: string | undefined
) => {
  const enabled = !!(productId && potSize && potType && potColor);
  
  const { data: variantImages = [], isLoading } = useQuery({
    queryKey: ['variant-images', productId, potSize, potType, potColor],
    queryFn: async () => {
      if (!productId || !potSize || !potType || !potColor) {
        return [];
      }
      return await variantImageService.getImagesByAttributes(
        productId,
        potSize,
        potType,
        potColor
      );
    },
    enabled,
    staleTime: 60_000,
    gcTime: 300_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    retryDelay: attempt => Math.min(1000 * (2 ** attempt), 4000),
    placeholderData: [],
  });

  const sanitizeUrl = (url: string) => {
    if (!url) return '';
    let u = url.trim();
    if (u.startsWith('//')) u = `https:${u}`;
    if (u.startsWith('http://')) u = u.replace('http://', 'https://');
    return u;
  };

  const imageUrls = useMemo(() =>
    variantImages
      .map((img: any) => sanitizeUrl(img.image_url))
      .filter((u: string) => typeof u === 'string' && (u.startsWith('https://') || u.startsWith('/')))
  , [variantImages]);

  const hasVariantImages = imageUrls.length > 0;

  return {
    variantImages,
    imageUrls,
    hasVariantImages,
    isLoading
  };
};
